import { EInvoiceCharacterLimitInfoWithCounter } from 'client/components/common/EInvoiceCharacterLimitInfo'
import { TextLineInterface } from 'client/Interfaces/Draft/TextLineInterface'
import { l } from 'client/Other/Translation'
import * as React from 'react'

import { deleteFreeText, getFreeTexts, saveFreeText } from '../../actions/freeTexts'
import { Container } from '../../Global/Container'
import { Controller } from '../../Global/Controller'
import { FreeTextInterface } from '../../Interfaces/FreeTextInterface'
import { FreeTextInput } from '../common/FreeTextInput/FreeTextInput'

export interface InvoiceTextLinePropsInterface extends TextLineInterface {
  onDelete?: (e: Event) => void
  onChange?: (values: TextLineInterface) => void
  onDrop?: (which: number) => void
  automation?: string
  columns?: number
  id?: number
  freeTexts?: FreeTextInterface[]
  readonly?: boolean
  invalid?: boolean
  showProductType: boolean

  // redux actions
  saveFreetext?: (freeText: FreeTextInterface) => void
  deleteFreetext?: (id: number) => void
}

export interface OwnStateInterface {
  dragOn: boolean
}

const mapDispatchToProps = (dispatch) => ({
  saveFreetext: async (freeText: FreeTextInterface) => {
    await dispatch(saveFreeText(freeText))
    dispatch(getFreeTexts())
  },
  deleteFreetext: async (id: number) => {
    await dispatch(deleteFreeText(id))
    dispatch(getFreeTexts())
  },
})

export const InvoiceTextLine = Controller<InvoiceTextLinePropsInterface>(
  (state) => ({
    freeTexts: state.freeTexts,
  }),
  mapDispatchToProps,
)(
  class InvoiceTextLine extends Container<InvoiceTextLinePropsInterface> {
    private values: TextLineInterface

    constructor(props: InvoiceTextLinePropsInterface) {
      super(props)

      this.state = {
        dragOn: false,
      }

      this.values = {
        content: props.content,
        type: 'description',
      }
    }

    public static defaultProps: InvoiceTextLinePropsInterface = {
      content: '',
      type: 'description',
    }

    private canDrag = false
    private reactToDrag(elem: HTMLElement) {
      if (elem.getAttribute('data-type') === 'DragHandle') {
        this.canDrag = true
      }
    }

    private findDropTarget(elem: HTMLElement): HTMLElement {
      while (elem.getAttribute('data-type') !== 'DropTarget') {
        elem = elem.parentElement
      }
      return elem
    }

    public render(): JSX.Element {
      const self = this
      const actions: JSX.Element[] = []
      let colspan = this.props?.columns
      if (!this.props.showProductType) {
        colspan--
      }
      if (this.props.onDelete && !this.props.readonly) {
        actions.push(
          <span
            className="delete"
            key="onDelete"
            data-automation={this.props.automation + '-delete'}
            onClick={this.props.onDelete.bind(this)}
          ></span>,
        )
      }

      return (
        <tr
          data-automation={this.props.automation}
          draggable={!this.props.readonly && this.state.dragOn}
          data-type="DropTarget"
          className="Controlled InvoiceTextLine"
          onDragOver={(e) => {
            const elem = self.findDropTarget(e.target as HTMLElement)
            elem.classList.add('Dropping')
            e.preventDefault()
          }}
          onDragLeave={(e) => {
            const elem = self.findDropTarget(e.target as HTMLElement)
            elem.classList.remove('Dropping')
            e.preventDefault()
          }}
          onDrop={(e) => {
            const elem = self.findDropTarget(e.target as HTMLElement)
            elem.classList.remove('Dropping')
            self.props.onDrop(parseInt(e.dataTransfer.getData('id'), 10))
            e.preventDefault()
          }}
          onDragStart={(e) => {
            if (!self.canDrag) {
              e.preventDefault()
            }
            e.dataTransfer.setData('id', '' + self.props.id)
          }}
          onMouseDown={(e) => {
            self.reactToDrag(e.target as HTMLElement)
          }}
        >
          <td
            data-automation={this.props.automation + '-dragHandle'}
            data-type="DragHandle"
            className="dragHandle"
            onMouseEnter={() => document.activeElement.blur()}
            onMouseOver={() => self.setState({ dragOn: true })}
            onMouseLeave={() => self.setState({ dragOn: false })}
          ></td>
          <td colSpan={colspan}>
            <FreeTextInput
              value={this.props.content}
              rows={4}
              className={['Description']}
              automation={this.props.automation + '-content'}
              values={this.props.freeTexts}
              selectButtonLabel={l('invoice.freetext.select')}
              saveButtonLabel={l('invoice.freetext.save')}
              selectHeader={l('invoice.freetext.modal.select')}
              closeButtonLabel={l('invoice.freetext.modal.close')}
              resizable={true}
              onSave={(value) => {
                this.props.saveFreetext({ id: 0, text: value, context: 'textrow' })
              }}
              onDelete={(id) => {
                this.props.deleteFreetext(id)
              }}
              onBlur={(content) => {
                if (content.length > 1024) {
                  self.props.onChange({ ...self.values, content, eInvoiceLimit: true })
                } else {
                  self.props.onChange({ ...self.values, content, eInvoiceLimit: false })
                }
              }}
              editable={!this.props.readonly}
              invalid={this.props.invalid}
            />
            {this.props.content.length > 1024 && (
              <EInvoiceCharacterLimitInfoWithCounter count={this.props.content.length} invalid={this.props.invalid} />
            )}
          </td>
          <td>{actions}</td>
        </tr>
      )
    }
  },
)
